<template>
    <section
        :id="slice.primary.anchorName ?? ''"
        ref="section"
        :class="[
            'slice slice-hero',
            `slice-hero--with-form`,
            {'slice-hero--hide-in-mobile': slice.primary.hideInMobile},
            {'slice-hero--with-form--form-has-background': slice.primary.withBackground},
        ]"
        aria-label="hero"
    >
        <div v-if="slice.primary.withBackground" class="slice-hero--with-form__polygon" />
        <div :class="['slice-hero--with-form__shapes', {'slice-hero--with-form__shapes--form-has-background': slice.primary.withBackground}]">
            <div class="slice-content text-left">
                <div class="slice-hero__content-wrapper">
                    <div class="slice-hero__content">
                        <PrismicRichText :field="slice.primary.title" class="slice-hero__title" />
                        <div class="hero-details">
                            <PrismicRichText :field="slice.primary.description" class="slice-hero__description" />
                            <div class="slice-hero--with-form__cta">
                                <VJoyButton
                                    v-if="isFilled.link(slice.primary.cta)"
                                    :href="slice.primary.cta.url ? $withLocalePrefix(slice.primary.cta.url) : undefined"
                                    variant="main"
                                    data-component-source="hero"
                                >
                                    {{ slice.primary.cta.text }}
                                </VJoyButton>
                            </div>
                        </div>
                    </div>
                    <div :class="['slice-hero--with-form__form-card', {'slice-hero--with-form__form-card--ready': formReady}]">
                        <h2
                            v-if="isFilled.keyText(slice.primary.hubspotFormTitle) && !isSubmittedForm"
                            class="slice-hero--with-form__form-card-title"
                        >
                            {{ slice.primary.hubspotFormTitle }}
                        </h2>
                        <div data-testid="slice-hero-form">
                            <MktHubspotForm
                                :region="slice.primary.hubspotRegion ?? ''"
                                :portal-id="slice.primary.hubspotPortalId ?? ''"
                                :form-id="slice.primary.hubspotFormId ?? ''"
                                :salesforce-id="slice.primary.salesforceId ?? ''"
                                @ready="onFormReady"
                                @submit="onFormSubmit"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {HeroSlice} from '@prismicio-types';
    import {MktHubspotForm} from '#components';
    import {useComponentTracking} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';

    type HeroWithForm = Extract<HeroSlice, {variation: 'heroWithForm'}>;
    defineProps(getSliceComponentProps<HeroWithForm>());

    const section = useTemplateRef('section');

    const formReady = ref(false);
    function onFormReady() {
        formReady.value = true;
    }
    const isSubmittedForm = ref(false);
    function onFormSubmit() {
        isSubmittedForm.value = true;
    }

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $flex-gap: 48px;

    .slice-hero {
        &--with-form {
            --hero-polygon-height-variation: 25px;
            --hero-polygon-background-size: auto 250px;
            --hero-background-position: 100% calc(100% + var(--joy-core-spacing-5));

            &--form-has-background {
                .slice-hero__description > p,
                .slice-hero__title > h1 {
                    color: var(--joy-color-neutral-0);
                }

                .slice-hero--with-form__cta {
                    .joy-button {
                        color: var(--joy-color-primary-50);
                        background-color: var(--joy-color-background-default);

                        &:hover {
                            background-color: var(--joy-color-neutral-20);
                        }
                    }
                }
            }

            &__cta {
                margin-top: var(--joy-core-spacing-10);
            }

            &__polygon {
                position: absolute;
                width: 100%;
                height: 100%;
                background-color: var(--joy-color-primary-50);
                background-repeat: no-repeat;
                z-index: -1;
                clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% calc(100% - var(--hero-polygon-height-variation)));
                background-image: url('../../../../assets/svg/hero-default-left-shapes.svg');
                background-position: left bottom;
                background-size: 100% auto;
            }

            &__shapes {
                background-image: url('../../../../assets/svg/home-page-hero-mobile-shapes.svg');
                background-repeat: no-repeat;
                background-position: right top -14px;
                background-size: contain;
                padding-top: 21px;

                &--form-has-background {
                    background-image: none;
                }

                @include mq.screen_md(min) {
                    background-image: url('../../../../assets/svg/home-page-hero-shapes.svg');
                    padding-top: 16px;

                    &--form-has-background {
                        background-image: none;
                    }
                }
            }

            .slice-hero__content-wrapper {
                flex-direction: column;
                align-items: flex-start;
                padding-bottom: 50px;
                gap: 44px;

                @include mq.screen_md(min) {
                    flex-direction: row;
                    gap: $flex-gap;
                }
            }

            .slice-hero__content {
                @include mq.screen_md(min) {
                    margin-top: var(--joy-core-spacing-6);
                    position: sticky;
                    top: 90px;
                    align-self: flex-start;
                }
            }

            &__form-card {
                position: relative;
                flex: none;
                background: var(--joy-color-background-default);
                width: 100%;
                max-width: 550px;
                margin: 0 auto;
                border-radius: var(--joy-core-radius-4);
                padding: var(--joy-core-spacing-6) var(--joy-core-spacing-5);
                min-height: 550px;
                opacity: 0;
                transition: all 0.3s ease;
                transform: scale(0.5);

                &--ready {
                    opacity: 0.9;
                    transform: scale(1);
                    min-height: auto;
                    box-shadow:
                        0 0 0 1px var(--joy-color-brand-primary-10),
                        0 0 3px var(--joy-color-brand-primary-10);

                    &:hover {
                        box-shadow: var(--joy-core-elevation-2);
                        opacity: 1;
                    }
                }

                h2 {
                    font-size: var(--joy-font-size-secondary-400);
                    font-family: var(--joy-font-family-title);
                    line-height: var(--joy-line-height-small);
                    margin-bottom: var(--joy-core-spacing-6);
                }

                @include mq.screen_md(min) {
                    padding: var(--joy-core-spacing-8) var(--joy-core-spacing-6);
                }
            }

            .hubspot-form {
                min-height: inherit;
            }

            .hs_submit input[type='submit'] {
                border: 1px solid var(--joy-color-primary-50);
                background-color: var(--joy-color-primary-50);

                &:hover {
                    background-color: var(--joy-color-primary-70);
                    border: 1px solid var(--joy-color-primary-70);
                }
            }

            .actions {
                float: left;
            }

            .hs-form-field:not(.hs-fieldtype-radio) label {
                display: none;
            }

            .hs-form-field input.invalid.error,
            .hs-form-field select.invalid.error,
            .hs-form-field textarea.invalid.error {
                border-color: var(--joy-color-primary-50);
            }

            .hs_error_rollup label {
                color: var(--joy-color-primary-50);
            }

            .hs-richtext {
                a {
                    color: var(--joy-color-text-default);
                    text-decoration: underline;
                    text-underline-offset: 3px;
                }
            }
        }
    }
</style>
